import {
  Badge, Box, Button, Flex, FormControl, FormErrorMessage, HStack, IconButton, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Text, Tooltip,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaPen, FaPlus, FaRedo, FaTrash } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Column } from 'react-table';
import TableComponent from 'components/TableComponent';
import CurrencyInput from 'components/CurrencyInput';
import SelectForm from 'components/SelectForm';
import FormLabelCustom from 'components/FormLabel';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import { applyPixKeyMask, PIX_KEY_TYPES, PixKeyType, pixKeyToRaw, validatePixKey } from './pixKey';
import { BATCH_TIMEZONE } from './parse';
import {
  addWithdrawalBatchItem, approveWithdrawalBatch, BatchItemPayload, confirmWithdrawalBatch,
  deleteWithdrawalBatch, deleteWithdrawalBatchItem, findWithdrawalBatchItems, getWithdrawalBatch,
  rejectWithdrawalBatch, retryWithdrawalBatch, updateWithdrawalBatchItem, WithdrawalBatchItem,
  WithdrawalBatchItemStatus,
} from 'services/api.service';
import { WithdrawalBatchStatusRender } from '.';
import { maskCurrency } from 'utils/number';

const ItemStatusRender: FC<{ status: WithdrawalBatchItemStatus }> = ({ status }) => {
  const map: Record<WithdrawalBatchItemStatus, [string, string]> = {
    PENDING: ['gray', 'Rascunho'],
    PROCESSING: ['blue', 'Processando'],
    SUCCEEDED: ['green', 'Pago'],
    FAILED: ['red', 'Falhou'],
    SCHEDULED: ['purple', 'Agendado'],
  };
  const [scheme, label] = map[status] ?? ['gray', status];
  return <Badge colorScheme={scheme}>{label}</Badge>;
};

const SCHEDULE_STATUS: Record<string, [string, string]> = {
  SCHEDULED: ['purple', 'Agendado'],
  PROCESSING: ['blue', 'Executando'],
  EXECUTED: ['green', 'Executado'],
  FAILED: ['red', 'Falhou'],
  CANCELED: ['gray', 'Cancelado'],
};

/**
 * O item do lote congela em "Agendado" quando vira agendamento — o lote fecha
 * com os imediatos. Para a linha não virar caixa-preta, mostramos aqui o status
 * VIVO do agendamento, que vem junto na listagem de itens.
 */
const ScheduleCell: FC<{ item: WithdrawalBatchItem }> = ({ item }) => {
  if (!item.scheduledFor) return <Text fontSize="sm" color="gray.500">Hoje</Text>;
  // O instante guardado é 08:00 em São Paulo; sem fixar a zona o navegador
  // renderiza no fuso dele e pode mostrar o dia errado.
  const date = DateTime.fromISO(item.scheduledFor).setZone(BATCH_TIMEZONE).toFormat('dd/MM/yyyy');
  const live = item.scheduledWithdrawal?.status;
  const [scheme, label] = (live && SCHEDULE_STATUS[live]) || ['purple', 'Agendado'];
  const lastError = item.scheduledWithdrawal?.lastError;
  return (
    <HStack spacing={2}>
      <Text fontSize="sm">{date}</Text>
      <Tooltip label={lastError ?? ''} hasArrow isDisabled={!lastError}>
        <Badge colorScheme={scheme}>{label}</Badge>
      </Tooltip>
    </HStack>
  );
};

type FormValues = {
  externalId: string;
  name: string;
  document: string;
  pixKeyType: string;
  pixKey: string;
  amount: number;
  description: string;
  scheduledFor: string;
};

const EMPTY_FORM: FormValues = { externalId: '', name: '', document: '', pixKeyType: '', pixKey: '', amount: 0, description: '', scheduledFor: '' };

function ItemModal({ isOpen, onClose, batchId, item }: { isOpen: boolean; onClose: () => void; batchId: string; item: WithdrawalBatchItem | null }) {
  const toast = useToast();
  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({ defaultValues: EMPTY_FORM });
  const pixKeyType = watch('pixKeyType');

  useEffect(() => {
    if (!item) {
      reset(EMPTY_FORM);
      return;
    }
    const rawType = item.pixKeyType ?? '';
    const type = PIX_KEY_TYPES.some((t) => t.value === rawType) ? rawType : '';
    reset({
      externalId: item.externalId,
      name: item.name ?? '',
      document: item.document ?? '',
      pixKeyType: type,
      pixKey: type ? applyPixKeyMask(item.pixKey, type) : item.pixKey,
      amount: Number(item.amount),
      description: item.description ?? '',
      // Pré-preenche o input date, e esse valor volta para a API: tem que ser o dia de São Paulo.
      scheduledFor: item.scheduledFor ? DateTime.fromISO(item.scheduledFor).setZone(BATCH_TIMEZONE).toFormat('yyyy-MM-dd') : '',
    });
  }, [item, isOpen, reset]);

  const { mutate, isLoading } = useMutation(
    (data: FormValues) => {
      const payload: BatchItemPayload = {
        externalId: data.externalId,
        pixKey: pixKeyToRaw(data.pixKey, data.pixKeyType as PixKeyType),
        amount: Number(data.amount),
        name: data.name || undefined,
        document: data.document || undefined,
        pixKeyType: data.pixKeyType || undefined,
        description: data.description || undefined,
        scheduledFor: data.scheduledFor || null,
      };
      return item ? updateWithdrawalBatchItem(batchId, item.id, payload) : addWithdrawalBatchItem(batchId, payload);
    },
    {
      onSuccess: () => {
        toast({ title: 'Item salvo', status: 'success', position: 'top' });
        queryClient.invalidateQueries({ queryKey: [`findWithdrawalBatchItems-${batchId}`] });
        queryClient.invalidateQueries({ queryKey: ['getWithdrawalBatch', batchId] });
        onClose();
      },
      onError: (e: any) => toast({ title: 'Erro', description: e?.response?.data?.message, status: 'error', position: 'top' }),
    },
  );

  const textField = (name: 'externalId' | 'name' | 'document' | 'description', label: string, required?: boolean) => (
    <Controller
      control={control}
      name={name}
      rules={required ? { required: 'Campo obrigatório' } : undefined}
      render={({ field: f, fieldState: { error } }) => (
        <FormControl isInvalid={!!error}>
          <FormLabelCustom label={label} name={name} props={{}} />
          <Input {...f} size="sm" bg="white" />
          <FormErrorMessage>{error?.message}</FormErrorMessage>
        </FormControl>
      )}
    />
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{item ? 'Editar item' : 'Adicionar item'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display="grid" gridGap={3}>
            {textField('externalId', 'Identificador', true)}
            {textField('name', 'Nome')}
            {textField('document', 'Documento')}

            <SelectForm
              control={control}
              name="pixKeyType"
              label="Tipo da chave"
              placeholder="Selecione o tipo"
              options={PIX_KEY_TYPES}
              rules={{ required: 'Selecione o tipo da chave' }}
              onChange={() => setValue('pixKey', '')}
            />

            {pixKeyType && (
              <Controller
                control={control}
                name="pixKey"
                rules={{
                  required: 'Campo obrigatório',
                  validate: (value) => {
                    if (!value) return 'Campo obrigatório';
                    return validatePixKey(value, pixKeyType as PixKeyType) ?? true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl isInvalid={!!error}>
                    <FormLabelCustom label="Chave Pix" name="pixKey" props={{}} />
                    <Input
                      {...field}
                      size="sm"
                      bg="white"
                      placeholder={
                        pixKeyType === 'CPF'
                          ? '000.000.000-00'
                          : pixKeyType === 'CNPJ'
                            ? '00.000.000/0000-00'
                            : pixKeyType === 'PHONE'
                              ? '(00) 00000-0000'
                              : pixKeyType === 'EMAIL'
                                ? 'email@exemplo.com'
                                : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                      }
                      maxLength={
                        pixKeyType === 'CPF' ? 14 : pixKeyType === 'CNPJ' ? 18 : pixKeyType === 'PHONE' ? 15 : undefined
                      }
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(
                          ['CPF', 'CNPJ', 'PHONE'].includes(pixKeyType) ? applyPixKeyMask(val, pixKeyType) : val,
                        );
                      }}
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />
            )}

            <CurrencyInput
              control={control}
              name="amount"
              label="Valor"
              placeholder="R$ 0,00"
              rules={{
                required: 'Campo obrigatório',
                validate: { positive: (v: number) => v > 0 || 'O valor deve ser maior que 0' },
              }}
            />

            {textField('description', 'Descrição')}

            <Controller
              control={control}
              name="scheduledFor"
              render={({ field: f }) => (
                <FormControl>
                  <FormLabelCustom label="Data de pagamento (opcional)" name="scheduledFor" props={{}} />
                  <Input {...f} type="date" size="sm" bg="white" />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Em branco, o pagamento sai hoje. Com data futura, é agendado para as 08:00 daquele dia.
                  </Text>
                </FormControl>
              )}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
          <Button colorScheme="brand" isLoading={isLoading} onClick={handleSubmit((d) => mutate(d))}>Salvar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function WithdrawalsBatchItems() {
  const { id = '' } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const { requestTwoFa } = useTwoFaHelper();
  const modal = useDisclosure();
  const [editing, setEditing] = useState<WithdrawalBatchItem | null>(null);

  const { data: batch, refetch } = useQuery(['getWithdrawalBatch', id], () => getWithdrawalBatch(id), { refetchInterval: 5000 });
  const isPending = batch?.status === 'PENDING';
  const isApproval = batch?.status === 'PENDING_APPROVAL';

  const invalidateAll = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: [`findWithdrawalBatchItems-${id}`] });
    queryClient.invalidateQueries({ queryKey: ['findWithdrawalBatches'] });
  };

  const ok = (title: string) => toast({ title, status: 'success', position: 'top' });
  const err = (e: any) => toast({ title: 'Erro', description: e?.response?.data?.message, status: 'error', position: 'top' });

  const { mutate: confirm, isLoading: confirming } = useMutation(
    async () => {
      const code = await requestTwoFa();
      if (!code?.id) throw new Error('2FA cancelado');
      return confirmWithdrawalBatch(id, code.id);
    },
    { onSuccess: () => { ok('Lote enviado'); invalidateAll(); }, onError: (e) => err(e) },
  );
  const { mutate: approve, isLoading: approving } = useMutation(() => approveWithdrawalBatch(id), { onSuccess: () => { ok('Lote aprovado'); invalidateAll(); }, onError: err });
  const { mutate: reject, isLoading: rejecting } = useMutation(() => rejectWithdrawalBatch(id), { onSuccess: () => { ok('Lote rejeitado'); invalidateAll(); }, onError: err });
  const { mutate: retry, isLoading: retrying } = useMutation(() => retryWithdrawalBatch(id), { onSuccess: (r: any) => { ok(`${r.requeued} item(s) reenfileirado(s)`); invalidateAll(); }, onError: err });
  const { mutate: discard, isLoading: discarding } = useMutation(() => deleteWithdrawalBatch(id), { onSuccess: () => { ok('Rascunho descartado'); navigate('/access/withdrawals-batch'); }, onError: err });

  const deleteItem = useMutation((itemId: string) => deleteWithdrawalBatchItem(id, itemId), { onSuccess: () => { ok('Item removido'); invalidateAll(); }, onError: err });

  const columns = useMemo(
    () =>
      [
        { Header: 'Identificador', accessor: 'externalId' },
        { Header: 'Nome', accessor: 'name' },
        { Header: 'Chave Pix', accessor: 'pixKey' },
        { Header: 'Valor', accessor: 'amount', Cell: ({ value }: any) => maskCurrency(+value) },
        {
          Header: 'Data de pagamento',
          accessor: 'scheduledFor',
          Cell: ({ row }: any) => <ScheduleCell item={row.original as WithdrawalBatchItem} />,
        },
        { Header: 'Status', accessor: 'status', Cell: ({ value }: any) => <ItemStatusRender status={value} /> },
        { Header: 'Erro', accessor: 'error', Cell: ({ value }: any) => <Text fontSize="xs" color="red.500">{value?.message ?? ''}</Text> },
        { Header: 'Ações', accessor: 'actions' },
      ] as Column[],
    [],
  );

  const queryFn = (filter: { page: number; limit: number; query?: Record<string, any> }) =>
    findWithdrawalBatchItems(id, { page: filter.page, limit: filter.limit, status: filter.query?.status });

  const rowActions = (row: WithdrawalBatchItem) =>
    isPending ? (
      <HStack>
        <IconButton aria-label="Editar" size="sm" variant="outline" icon={<FaPen />} onClick={() => { setEditing(row); modal.onOpen(); }} />
        <IconButton aria-label="Remover" size="sm" variant="outline" colorScheme="red" icon={<FaTrash />} onClick={() => deleteItem.mutate(row.id)} />
      </HStack>
    ) : null;

  return (
    <Box>
      <SimpleGrid columns={{ sm: 1 }} spacing="20px">
        <Box>
          <HStack mb={4} justify="space-between" flexWrap="wrap" spacing={4}>
            <Box>
              <HStack spacing={3} mb={1}>
                <Text fontSize="2xl" fontWeight="bold">Lote #{id.slice(0, 8)}</Text>
                {batch && <WithdrawalBatchStatusRender status={batch.status} />}
              </HStack>
              <Text color="gray.500" fontSize="sm">
                {batch?.createdAt ? DateTime.fromISO(batch.createdAt).toFormat('dd/MM/yyyy HH:mm') : ''} ·{' '}
                {batch ? maskCurrency(+batch.totalAmount) : ''}
              </Text>
            </Box>
            <HStack spacing={3} flexWrap="wrap">
              {batch && (
                <Flex gap={6}>
                  <Box textAlign="center"><Text fontSize="xs" color="gray.500">Total</Text><Text fontWeight="bold">{batch.totalItems}</Text></Box>
                  <Box textAlign="center"><Text fontSize="xs" color="gray.500">Sucesso</Text><Text fontWeight="bold" color="green.500">{batch.succeededItems}</Text></Box>
                  <Box textAlign="center"><Text fontSize="xs" color="gray.500">Falhas</Text><Text fontWeight="bold" color="red.500">{batch.failedItems}</Text></Box>
                </Flex>
              )}
              {isPending && (
                <>
                  <Button leftIcon={<FaPlus />} variant="outline" size="sm" onClick={() => { setEditing(null); modal.onOpen(); }}>Adicionar item</Button>
                  <Button variant="ghost" colorScheme="red" size="sm" isLoading={discarding} onClick={() => discard()}>Descartar</Button>
                  <Button colorScheme="brand" size="sm" isLoading={confirming} onClick={() => confirm()}>Confirmar e enviar</Button>
                </>
              )}
              {isApproval && (
                <>
                  <Button colorScheme="red" variant="outline" size="sm" isLoading={rejecting} onClick={() => reject()}>Rejeitar</Button>
                  <Button colorScheme="green" size="sm" isLoading={approving} onClick={() => approve()}>Aprovar</Button>
                </>
              )}
              {batch && batch.failedItems > 0 && batch.status !== 'PENDING' && batch.status !== 'PENDING_APPROVAL' && (
                <Button leftIcon={<FaRedo />} colorScheme="orange" size="sm" isLoading={retrying} onClick={() => retry()}>
                  Reprocessar falhas ({batch.failedItems})
                </Button>
              )}
            </HStack>
          </HStack>

          <TableComponent label="Itens do Lote" columnsData={columns} queryFn={queryFn} queryKey={`findWithdrawalBatchItems-${id}`} actions={rowActions} />
        </Box>
      </SimpleGrid>

      <ItemModal isOpen={modal.isOpen} onClose={modal.onClose} batchId={id} item={editing} />
    </Box>
  );
}
